import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Steps, Button, Input, Alert, Space, Typography, message, Spin } from 'antd';
import { CopyOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { authAPI } from '../../services/api';

const { Title, Paragraph, Text } = Typography;

type SetupData = { otpauth_url: string; qr_data_url: string; manual_entry_key: string };

const MfaSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authAPI.mfaSetup()
      .then((data: SetupData) => { if (!cancelled) setSetupData(data); })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to start MFA setup');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleVerify = async () => {
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await authAPI.mfaEnable(trimmed);
      setRecoveryCodes(result.recovery_codes || []);
      setStep(2);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    message.success('Recovery codes copied to clipboard');
  };

  // 那个按钮本来就 disabled={!acknowledged}，这句提示根本走不到。
  const finish = () => navigate('/admin');

  return (
    <div style={{ maxWidth: 720, margin: '40px auto', padding: '0 16px' }}>
      <Card>
        <Title level={3}>
          <SafetyCertificateOutlined /> Set up two-factor authentication
        </Title>
        <Paragraph type="secondary">
          Platform admin accounts require MFA. Once enabled, every login on this account
          will need a 6-digit code from an authenticator app (Apple Passwords, 1Password,
          Google Authenticator, Authy, etc.) in addition to your password.
        </Paragraph>

        <Steps
          current={step}
          items={[
            { title: 'Scan QR code' },
            { title: 'Verify code' },
            { title: 'Save recovery codes' },
          ]}
          style={{ margin: '24px 0' }}
        />

        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

        {step === 0 && (
          <>
            {loading || !setupData ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (
              <>
                <Paragraph>
                  Open your authenticator app and scan the QR code below. If you can't scan,
                  enter the key manually.
                </Paragraph>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                  <img src={setupData.qr_data_url} alt="MFA QR" style={{ width: 220, height: 220 }} />
                </div>
                <Alert
                  type="info"
                  showIcon
                  message="Manual entry key"
                  description={
                    <Space>
                      <Text code copyable={{ text: setupData.manual_entry_key }}>
                        {setupData.manual_entry_key}
                      </Text>
                    </Space>
                  }
                  style={{ marginBottom: 24 }}
                />
                <Button type="primary" block onClick={() => setStep(1)}>
                  I've added it to my authenticator
                </Button>
              </>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <Paragraph>
              Enter the 6-digit code currently shown in your authenticator app.
            </Paragraph>
            <Input
              size="large"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              style={{ textAlign: 'center', letterSpacing: 8, fontSize: 22, marginBottom: 16 }}
              autoFocus
              onPressEnter={handleVerify}
            />
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={() => { setStep(0); setCode(''); setError(null); }}>
                Back
              </Button>
              <Button type="primary" loading={loading} onClick={handleVerify}>
                Verify and enable
              </Button>
            </Space>
          </>
        )}

        {step === 2 && (
          <>
            <Alert
              type="warning"
              showIcon
              message="Save these recovery codes now"
              description={
                <>
                  Each code can be used once if you lose access to your authenticator app.
                  <strong> They will NOT be shown again.</strong> Store them in your password
                  manager or print them.
                </>
              }
              style={{ marginBottom: 16 }}
            />
            <div
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 16,
                fontFamily: 'monospace',
                fontSize: 16,
                lineHeight: 1.8,
                marginBottom: 16,
              }}
            >
              {recoveryCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
              <Button icon={<CopyOutlined />} onClick={copyRecoveryCodes}>
                Copy all
              </Button>
              <Button onClick={() => window.print()}>Print</Button>
            </Space>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>I have saved the recovery codes in a safe place</span>
            </label>
            <Button type="primary" block size="large" disabled={!acknowledged} onClick={finish}>
              Continue to admin
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default MfaSetupPage;
